import { useMemo, useState } from 'react';
import { Button, Table, TableBody, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { isAxiosError } from 'axios';
import { FaPlus } from 'react-icons/fa6';
import { useSearchParams } from 'react-router-dom';
import { UserFilter, UserSortField } from 'api/model';
import useUsers from 'api/users/useUsers';
import useAuth from 'auth/useAuth';
import Loader from 'components/Loader';
import { SettingsDetailPanel } from 'components/settingsHub/SettingsDetailPanel';
import { SettingsSection } from 'components/settingsHub/SettingsSection';
import { PrimaryActionButton, ScopeButton, ScopeGroup } from 'components/settingsHub/settingsEntities.styles';
import { useDebounce } from 'hooks/useDebounce';
import { useKeyboard } from 'hooks/useKeyboard';
import UserAdd from './UserAdd';
import UserRow from './UserRow';
import { EmptyState, HeadCell, TableFooterBar, UsersTableCard } from './UsersView.styles';

type StatusFilter = 'all' | 'active' | 'inactive';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const COLUMNS: { field: UserSortField; label: string }[] = [
  { field: 'username', label: 'Username' },
  { field: 'display_name', label: 'Display name' },
  { field: 'is_superuser', label: 'Role' },
  { field: 'is_active', label: 'Status' },
  { field: 'created_at', label: 'Created' },
  { field: 'last_login_at', label: 'Last sign in' },
];

const UsersView = () => {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<UserSortField>('username');
  const [sortDescending, setSortDescending] = useState(false);
  const [addUserFormOpen, setAddUserFormOpen] = useState(false);

  const debouncedSearch = useDebounce(search.trim());

  const sort = useMemo(() => [`${sortDescending ? '-' : ''}${sortField}`], [sortField, sortDescending]);

  const filters = useMemo<UserFilter[]>(() => {
    const activeFilters: UserFilter[] = [];
    if (debouncedSearch) activeFilters.push({ field: 'username', operator: 'ilike', value: `%${debouncedSearch}%` });
    if (status !== 'all')
      activeFilters.push({ field: 'is_active', operator: 'eq', value: String(status === 'active') });
    return activeFilters;
  }, [debouncedSearch, status]);

  const { users, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } = useUsers({ sort, filters });

  useKeyboard({
    keyDown: (e) => {
      if (e.key !== 'Escape' || !searchParams.get('config')) return;
      if (addUserFormOpen) {
        setAddUserFormOpen(false);
      } else {
        setSearchParams({});
      }
    },
  });

  const toggleSort = (field: UserSortField) => {
    if (field === sortField) {
      setSortDescending((previous) => !previous);
      return;
    }
    setSortField(field);
    setSortDescending(false);
  };

  if (isAxiosError(error) && error.response?.status === 403) {
    return (
      <EmptyState>
        <Typography>You need superuser permissions to manage users.</Typography>
      </EmptyState>
    );
  }

  if (isLoading) return <Loader />;

  return (
    <>
      <SettingsSection
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by username…"
        count={users.length}
        countNoun={hasNextPage ? 'users loaded' : 'users'}
        filters={
          <ScopeGroup role="group" aria-label="Status filter">
            {STATUS_FILTERS.map(({ value, label }) => (
              <ScopeButton
                key={value}
                type="button"
                selected={status === value}
                aria-pressed={status === value}
                onClick={() => setStatus(value)}
              >
                {label}
              </ScopeButton>
            ))}
          </ScopeGroup>
        }
        actions={
          <PrimaryActionButton type="button" onClick={() => setAddUserFormOpen(true)}>
            <FaPlus size={12} />
            Add User
          </PrimaryActionButton>
        }
      >
        <UsersTableCard>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map(({ field, label }) => (
                  <HeadCell key={field} sortDirection={sortField === field ? (sortDescending ? 'desc' : 'asc') : false}>
                    <TableSortLabel
                      active={sortField === field}
                      direction={sortField === field && sortDescending ? 'desc' : 'asc'}
                      onClick={() => toggleSort(field)}
                    >
                      {label}
                    </TableSortLabel>
                  </HeadCell>
                ))}
                <HeadCell align="right">Actions</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <UserRow key={user.id} user={user} isSelf={user.id === currentUser?.id} />
              ))}
            </TableBody>
          </Table>

          {!users.length && (
            <EmptyState>
              <Typography>No users match the current filters.</Typography>
            </EmptyState>
          )}

          {hasNextPage && (
            <TableFooterBar>
              <Button size="small" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            </TableFooterBar>
          )}
        </UsersTableCard>
      </SettingsSection>

      {addUserFormOpen && (
        <SettingsDetailPanel onClose={() => setAddUserFormOpen(false)}>
          <UserAdd onCancel={() => setAddUserFormOpen(false)} onSubmit={() => setAddUserFormOpen(false)} />
        </SettingsDetailPanel>
      )}
    </>
  );
};

export default UsersView;
